import { BulkImport, type BulkImportConfig, type Preview } from '../components/BulkImport';

/** The staff dry run answers one question the student one does not: which departments and
 *  designations in this file does the school not have yet? */
interface StaffPreview extends Preview {
  unknownDepartments: string[];
  unknownDesignations: string[];
}

const config: BulkImportConfig<StaffPreview> = {
  eyebrow: 'Staff',
  title: 'Import staff from a spreadsheet',
  intro: (
    <>
      The same three steps as the student import. Nothing is saved until you have seen exactly what
      will happen and pressed <strong>Import</strong>.
    </>
  ),
  basePath: '/api/staff/import',
  templateFileName: 'eduvora-staff-template.csv',
  noun: 'member of staff',
  nounPlural: 'staff',
  uploadHint: (
    <>
      Save as <strong>CSV UTF-8</strong> from Excel, not .xlsx. Only the employee code, the name and
      the <strong>joining date</strong> are required — service and leave are both counted from it,
      so there is no sensible default.
    </>
  ),
  listTo: '/hr',
  listLabel: 'staff list',

  // ⚠ SHOWN ABOVE THE PROBLEM TABLES, because it is the one thing on this screen the school can act
  // on before importing rather than after. The same names also appear as warnings in the table
  // below; this panel exists so they are not lost among forty rows saying the same thing.
  extraSummary: (p) => {
    const dept = p.unknownDepartments ?? [];
    const desig = p.unknownDesignations ?? [];
    if (dept.length === 0 && desig.length === 0) return null;
    return (
      <div className="card" style={{
        padding: 14, marginBottom: 14, background: 'var(--bg-2, #f7f8fa)',
        borderLeft: '3px solid var(--warn, #b54708)',
      }}>
        <div style={{ fontWeight: 650, fontSize: 13.5, marginBottom: 4 }}>
          Some of these do not exist at this school yet
        </div>
        <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: '0 0 8px', maxWidth: '64ch' }}>
          {/* ⚠ Says what happens if they carry on, not just that something is missing. Nobody
              creates eleven records on the strength of a warning that does not say why. */}
          Everyone still imports — the field is simply left blank. Create these first and upload the
          same file again, and they will be filled in.
        </p>
        {dept.length > 0 && <NameList label="Departments" names={dept} />}
        {desig.length > 0 && <NameList label="Designations" names={desig} />}
      </div>
    );
  },
};

function NameList({ label, names }: { label: string; names: string[] }) {
  return (
    <div style={{ fontSize: 13, marginTop: 4 }}>
      <span style={{ color: 'var(--ink-3)' }}>{label}: </span>
      {/* ⚠ Each name as typed in THEIR file, so it can be copied straight into the form that
          creates it — retyping is how "Science" becomes "Sciece". */}
      {names.map((n, i) => (
        <span key={n}>
          <code style={{ fontSize: 12.5 }}>{n}</code>{i < names.length - 1 ? ', ' : ''}
        </span>
      ))}
    </div>
  );
}

export function StaffImportPage() {
  return <BulkImport config={config} />;
}
